import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import { ChevronDown, Server } from 'lucide-react';
import {
  DeploymentCurrentRecord,
  DeploymentRecord,
  DeploymentServerGroup,
  DeploymentTargetRecord,
} from '../../types/deployment';
import { CurrentDeploymentRow } from './CurrentDeploymentRow';
import { DeploymentHistoryState } from './DeploymentHistoryPanel';
import {
  formatDate,
  getCustomerName,
  getServerName,
} from './deploymentExplorerUtils';

interface ServerDeploymentGroupProps {
  group: DeploymentServerGroup;
  expanded: boolean;
  targets: DeploymentTargetRecord[];
  saving?: boolean;
  historyStateBySlice: Record<string, DeploymentHistoryState | undefined>;
  openHistoryBySlice: Record<string, boolean | undefined>;
  onToggleGroup: () => void;
  onToggleHistory: (deploymentRecord: DeploymentCurrentRecord) => void;
  onLoadMoreHistory: (deploymentRecord: DeploymentCurrentRecord) => void;
  onRefreshHistory: (deploymentRecord: DeploymentCurrentRecord) => void;
  onOpenDeploymentDetails: (deploymentId: string) => void;
  onRefreshDeployment: (deploymentId: string) => void;
  onComposeStart: (deploymentId: string) => void;
  onComposeStop: (deploymentRecord: DeploymentRecord) => void;
  onOpenRestart: (deploymentRecord: DeploymentRecord) => void;
  onOpenRollback: (deploymentRecord: DeploymentRecord) => void;
}

const statusCount = (value?: number) => (value && value > 0 ? value : 0);

export const ServerDeploymentGroup: React.FC<ServerDeploymentGroupProps> = ({
  group,
  expanded,
  targets,
  saving = false,
  historyStateBySlice,
  openHistoryBySlice,
  onToggleGroup,
  onToggleHistory,
  onLoadMoreHistory,
  onRefreshHistory,
  onOpenDeploymentDetails,
  onRefreshDeployment,
  onComposeStart,
  onComposeStop,
  onOpenRestart,
  onOpenRollback,
}) => {
  const failedCount = statusCount(group.currentStatusCounts.failed);
  const runningCount = (
    statusCount(group.currentStatusCounts.requested)
    + statusCount(group.currentStatusCounts.pipeline_created)
    + statusCount(group.currentStatusCounts.manual_job_pending)
    + statusCount(group.currentStatusCounts.running)
  );

  return (
    <Accordion expanded={expanded} onChange={onToggleGroup} disableGutters>
      <AccordionSummary expandIcon={<ChevronDown size={18} />}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          justifyContent="space-between"
          alignItems={{ md: 'center' }}
          sx={{ width: '100%' }}
        >
          <Stack spacing={0.4}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Server size={16} />
              <Typography variant="subtitle1" fontWeight={700}>
                {getServerName(group.server)}
              </Typography>
              <Chip size="small" variant="outlined" label={getCustomerName(group.customer)} />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {group.deploymentSliceCount} current slices • {group.historicalDeploymentCount} historical deploys • Last activity {formatDate(group.latestActivityAt)}
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {failedCount > 0 ? (
              <Chip size="small" color="error" label={`${failedCount} failed`} />
            ) : null}
            {runningCount > 0 ? (
              <Chip size="small" color="info" label={`${runningCount} active`} />
            ) : null}
            <Chip size="small" variant="outlined" label={`${group.totalDeploymentCount} total records`} />
            {group.hiddenCurrentDeploymentCount > 0 ? (
              <Chip size="small" variant="outlined" label={`+${group.hiddenCurrentDeploymentCount} more slices`} />
            ) : null}
          </Stack>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={1.5}>
          {group.currentDeployments.map((deploymentRecord) => (
            <CurrentDeploymentRow
              key={deploymentRecord._id}
              deploymentRecord={deploymentRecord}
              targets={targets}
              saving={saving}
              historyOpen={Boolean(openHistoryBySlice[deploymentRecord.sliceKey])}
              historyState={historyStateBySlice[deploymentRecord.sliceKey]}
              onToggleHistory={() => onToggleHistory(deploymentRecord)}
              onLoadMoreHistory={() => onLoadMoreHistory(deploymentRecord)}
              onRefreshHistory={() => onRefreshHistory(deploymentRecord)}
              onOpenDeploymentDetails={onOpenDeploymentDetails}
              onRefreshDeployment={onRefreshDeployment}
              onComposeStart={onComposeStart}
              onComposeStop={onComposeStop}
              onOpenRestart={onOpenRestart}
              onOpenRollback={onOpenRollback}
            />
          ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};
